import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { StripedWordmark, TricolorStrip } from '../components/BSCAuthLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  LogOut, Package, Camera, Wrench, AlertTriangle, FileText, History, QrCode,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mobile-first employee self-service:
 *  - Shows assets currently assigned to me
 *  - Lets me raise Maintenance or Damage requests
 *  - Quick link to camera QR scanner
 */
export default function MyAssets() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ staff: null, current: [], history: [] });
  const [reqOpen, setReqOpen] = useState(null); // selected asset
  const [reqForm, setReqForm] = useState({ kind: 'maintenance', description: '', severity: 'medium' });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assets/mine`, { withCredentials: true });
      setData(r.data || { current: [], history: [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    if (!reqForm.description.trim()) { toast.error('Describe the issue first'); return; }
    try {
      await axios.post(`${API}/assets/mine/request`, {
        asset_id: reqOpen.id, ...reqForm,
      }, { withCredentials: true });
      toast.success('Request submitted — admin will be notified.');
      setReqOpen(null); setReqForm({ kind: 'maintenance', description: '', severity: 'medium' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F5F0' }}>
      <div className="border-b" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-to-launcher">
            <img src="/bsc-logo.jpeg" alt="BSC" className="w-9 h-9 rounded-lg" />
            <div className="flex flex-col leading-tight">
              <StripedWordmark text="MY ASSETS" size="sm" />
              <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Employee Self-Service</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-white/70 text-xs hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="logout-btn">
              <LogOut size={14} className="mr-1" />Logout
            </Button>
          </div>
        </div>
        <TricolorStrip />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="flex justify-between items-end mb-4">
          <div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>Hi {data.staff?.name || user?.name} 👋</h1>
            <p className="text-xs" style={{ color: '#5C5C5C' }}>
              {data.staff?.emp_id ? `Emp ID: ${data.staff.emp_id} · ` : ''}
              {data.current.length} asset(s) currently with you
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/scan-qr')} data-testid="open-scanner">
            <Camera size={14} className="mr-1" />Scan QR
          </Button>
        </div>

        {!data.staff && (
          <div className="bg-white border rounded-lg p-4 mb-4" style={{ borderColor: '#FEE' }} data-testid="no-staff">
            <p className="text-sm flex items-center gap-2" style={{ color: '#A43E33' }}>
              <AlertTriangle size={14} />Your user account isn't linked to a staff record yet. Ask admin to add you in People.
            </p>
          </div>
        )}

        {/* Current assets */}
        <h2 className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5C5C5C' }}>Currently With Me</h2>
        {data.current.length === 0 ? (
          <div className="bg-white border rounded-lg p-6 text-center text-sm mb-6" style={{ borderColor: '#E8E5DF', color: '#5C5C5C' }}>
            No assets are assigned to you right now.
          </div>
        ) : (
          <div className="space-y-2 mb-6">
            {data.current.map(a => (
              <div key={a.id} className="bg-white border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ borderColor: '#E8E5DF' }} data-testid={`mine-${a.asset_code}`}>
                <div className="flex items-start gap-3">
                  <div className="rounded-lg flex items-center justify-center" style={{ width: 44, height: 44, backgroundColor: '#F7F5F0' }}>
                    <Package size={20} style={{ color: '#1E3B29' }} />
                  </div>
                  <div>
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs font-mono" style={{ color: '#5C5C5C' }}>{a.asset_code}</div>
                    <div className="text-[10px] uppercase mt-0.5" style={{ color: '#5C5C5C' }}>
                      {a.brand || ''} {a.model ? `· ${a.model}` : ''} {a.branch ? `· ${a.branch}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setReqOpen(a); setReqForm({ kind: 'maintenance', description: '', severity: 'medium' }); }} data-testid={`req-maint-${a.asset_code}`}>
                    <Wrench size={12} className="mr-1" />Maintenance
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setReqOpen(a); setReqForm({ kind: 'damage', description: '', severity: 'high' }); }} data-testid={`req-dmg-${a.asset_code}`} style={{ color: '#A43E33', borderColor: '#A43E33' }}>
                    <AlertTriangle size={12} className="mr-1" />Damage
                  </Button>
                  <a href={`/scan/asset/${a.asset_code}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" data-testid={`view-qr-${a.asset_code}`}><QrCode size={12} /></Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History */}
        <h2 className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: '#5C5C5C' }}>
          <History size={12} />Assignment History
        </h2>
        {data.history.length === 0 ? (
          <p className="text-sm" style={{ color: '#5C5C5C' }}>Nothing yet.</p>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: '#E8E5DF' }}>
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: '#F7F5F0' }}>
                <tr><th className="text-left p-2">Asset</th><th className="text-left p-2">From</th><th className="text-left p-2">Status</th><th className="text-left p-2">Return</th></tr>
              </thead>
              <tbody>
                {data.history.map(h => (
                  <tr key={h.id} className="border-t" style={{ borderColor: '#E8E5DF' }}>
                    <td className="p-2 font-mono text-xs">{h.asset_code}</td>
                    <td className="p-2 text-xs">{h.assigned_at ? new Date(h.assigned_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="p-2 text-xs uppercase" style={{ color: h.status === 'active' ? '#1565C0' : '#5C5C5C' }}>{h.status}</td>
                    <td className="p-2 text-xs">{h.return_condition || '—'}{h.damage_cost ? ` · ₹${h.damage_cost}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Request dialog */}
      <Dialog open={!!reqOpen} onOpenChange={(v) => !v && setReqOpen(null)}>
        <DialogContent data-testid="req-dialog">
          {reqOpen && <>
            <DialogHeader>
              <DialogTitle>{reqForm.kind === 'maintenance' ? 'Request Maintenance' : 'Report Damage'}</DialogTitle>
              <DialogDescription>Asset: <strong>{reqOpen.asset_code} · {reqOpen.name}</strong></DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Severity</Label>
                <Select value={reqForm.severity} onValueChange={(v) => setReqForm({ ...reqForm, severity: v })}>
                  <SelectTrigger data-testid="req-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>What's the issue? *</Label>
                <textarea className="w-full border rounded p-2 text-sm" rows={4}
                  value={reqForm.description}
                  onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })}
                  data-testid="req-desc"
                  placeholder="Describe the problem in plain language…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReqOpen(null)}>Cancel</Button>
              <Button onClick={submitRequest} data-testid="req-submit" style={{ backgroundColor: reqForm.kind === 'damage' ? '#A43E33' : '#1E3B29', color: '#FFF' }}>
                <FileText size={12} className="mr-1" />Submit Request
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
